import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAvailableCompanies } from '@/hooks/useAvailableCompanies'

/**
 * Empresa em nome da qual o problema é reportado no formulário do
 * Atendimento HUB, para quem tem 2+ vínculos:
 *
 *  - começa na empresa principal; se ela não estiver entre as opções (ou não
 *    houver principal), na primeira — o select nunca mostra uma empresa e
 *    envia outra;
 *  - `processCompanyId` (processo vinculado) puxa a empresa só quando ELE
 *    muda — uma escolha manual não é sobrescrita por re-render ou por o
 *    processo terminar de carregar depois.
 *
 * O backend confere de novo (`resolveReportCompanyId`).
 */
export function useReportCompany(processCompanyId: number | null = null) {
  const { user } = useAuth()
  const { options } = useAvailableCompanies()
  const [companyId, setCompanyId] = useState<number | null>(
    user?.primary_company_id ?? null,
  )
  const showSelector = options.length > 1

  useEffect(() => {
    if (!showSelector) return
    if (companyId == null || !options.some((o) => o.id === companyId)) {
      setCompanyId(options[0].id)
    }
  }, [showSelector, options, companyId])

  const lastProcessCompany = useRef<number | null>(null)
  useEffect(() => {
    if (processCompanyId === lastProcessCompany.current) return
    lastProcessCompany.current = processCompanyId
    if (
      processCompanyId != null &&
      options.some((o) => o.id === processCompanyId)
    ) {
      setCompanyId(processCompanyId)
    }
  }, [processCompanyId, options])

  return { options, companyId, setCompanyId, showSelector }
}
